<script setup lang="ts">
import { ref, reactive, watch, computed } from "vue";
import CustomButton from "@/components/Button/Button.vue";
import CustomModal from "@/components/CustomModal/CustomModal.vue";
import { useI18n } from 'vue-i18n';
import { useTripsStore } from "@/stores/tripsStore";
import DateInput from "@/components/Global/DateInput.vue";
import * as DateUtils from "@/helpers/dateUtils";
import moment from 'moment';
import DestinationInput from "@/components/GlobalHeader/DestinationInput.vue";
import { useRouter } from 'vue-router';
import {VueSpinner} from "vue3-spinners";
import {useLocation} from "@/composables/useLocation";

const { t } = useI18n();
const tripsStore = useTripsStore();

const showAddTripModal = defineModel<boolean>('show')
const tripExists = ref(false);

const emit = defineEmits(['on-trip-created'])

const props = defineProps({
  show: Boolean,
  dateFrom: Date,
  dateTo: Date,
  yachtLocation: Object,
  yacht: Object,
  amenities: Array,
  selectedAmenities: Array
});

const { normalizeLocation } = useLocation();

const tripName = ref('');
const isCreatingTrip = ref(false);
const selectedLocation = ref<any[]>(normalizeLocation(props.yachtLocation) || []);
const formError = ref('');
const today = moment();
let nextSaturday = today.clone();
const daysUntilSaturday = (6 - today.day() + 7) % 7 || 7;
nextSaturday.add(daysUntilSaturday, 'days');
const followingSaturday = nextSaturday.clone().add(7, 'days');

const router = useRouter();
const createdSuccess = ref(false);
const createdTripSlug = ref<string | null>(null);

const prefillLabel = computed(() => {
  const y = props.yachtLocation as any;
  if (!y) return '';
  return y.label || y.name || y.city?.name || y.region?.name || y.country?.name || '';
});

const isPrefilledLocation = computed(() => {
  const y = props.yachtLocation as any;
  if (!y) return false;
  if (Array.isArray(y)) return y.length > 0;
  if (typeof y === 'string') return y.includes(':');
  return !!(y.value || y.id || y.city?.id || y.region?.id || y.country?.id);
});
const isPrefilledDates = computed(() => !!props.dateFrom && !!props.dateTo);

const selectedDates = reactive({
  dateFrom: props.dateFrom ?? nextSaturday.toDate(),
  dateTo: props.dateTo ?? followingSaturday.toDate()
});


function prefillFromProps() {
  if (props.dateFrom) selectedDates.dateFrom = props.dateFrom;
  if (props.dateTo) selectedDates.dateTo = props.dateTo;
  if (props.yachtLocation) selectedLocation.value = normalizeLocation(props.yachtLocation) || [];
}

watch(() => props.show, (isOpen) => {
  if (isOpen) prefillFromProps();
});

watch(() => [props.dateFrom, props.dateTo, props.yachtLocation], () => {
  if (showAddTripModal.value) prefillFromProps();
});


// Ensure discount is a single number
function normalizeDiscount(input) {
  if (input == null) return null;
  let val = input;
  if (Array.isArray(val)) {
    val = val[0];
  }
  if (typeof val === 'string') {
    val = val.replace('%', '').trim();
  }
  const num = Number(val);
  return Number.isFinite(num) ? num : null;
}

const onBook = async () => {
  formError.value = '';

  if (!tripName.value.trim()) {
    formError.value = t('tripAddModal.nameRequired');
    return;
  }

  if (!Array.isArray(selectedLocation.value) || selectedLocation.value.length === 0 || !selectedLocation.value[0].includes(':')) {
    formError.value = t('tripAddModal.locationRequired');
    return;
  }

  const start = selectedDates.dateFrom;
  const end = selectedDates.dateTo;
  if (!start || !end) {
    formError.value = t('tripAddModal.datesRequired');
    return;
  }
  isCreatingTrip.value = true;

  try {
    const start = selectedDates.dateFrom;
    const end = selectedDates.dateTo;
    if (!start || !end) return;

    const [entity, id] = selectedLocation.value?.[0]?.split(':') || [];
    const startStr = DateUtils.format(selectedDates.dateFrom);
    const endStr = DateUtils.format(selectedDates.dateTo);

    // Build initial offer if yacht data is provided (as per Option 2)
    const y: any = props.yacht || {};
    const offer = y
      ? {
          yachtId: y?.yacht?.id ?? y?.id,
          name: y?.yacht?.name ?? y?.name ?? '',
          basePrice: y?.price ?? y?.basePrice ?? 0,
          discount: normalizeDiscount(y?.discount),
          offerPrice: y?.finalPrice ?? y?.offerPrice ?? y?.price ?? 0,
          dateStart: startStr,
          dateEnd: endStr,
          destination: Number(id) || id,
          amenities: Array.isArray(props.amenities) ? props.amenities : [],
          passengers: y?.passengers,
          selectedAmenities: props.selectedAmenities
        }
      : null;

    const payload = {
      name: tripName.value,
      dateStart: startStr,
      dateEnd: endStr,
      locationId: id,
      // locationType: entity?.charAt(0).toUpperCase() + entity?.slice(1)
      locationType: 'Harbour' //We force it here because the availability does not provide anything.
    };

    console.log("Creating trip with payload (minimal):", payload);
    const tripRes = await tripsStore.createTrip(payload);
    const tripSlug = tripRes?.slug;
    console.log("CreateTrip response:", tripRes);

    if (!tripSlug) {
      throw new Error("Trip created but no slug returned");
    }

    await tripsStore.patchOfferToTrip(tripSlug, offer);
    console.log("Offer attached successfully (trip add modal)", offer);
    
    // emit('on-trip-created'); // Removed: do not trigger parent auto-close immediately

    // Keep modal open and show post-create actions
    createdTripSlug.value = tripSlug;
    createdSuccess.value = true;
  } catch (error: any) {
    const apiData = error?.response?.data;
    tripExists.value = false;

    if (apiData?.httpCode === 400 && apiData?.errorCode) {
      switch (apiData.errorCode) {
        case 105:
          // Trip name already exists
          formError.value = t('tripAddModal.tripAlreadyExists', { tripName: apiData.key });
          tripExists.value = true;
          break;

          // Handle other error codes here 
        default:
          formError.value = t('tripAddModal.createdFail');
      }
    } else {
      formError.value = t('tripAddModal.createdFail');
    }

    showAddTripModal.value = true;
    console.error("Error creating trip:", error);
  } finally {
    isCreatingTrip.value = false;
  }
};


// Body scroll lock if popup displayed
watch(showAddTripModal, (newValue) => {
  if (newValue) {
    document.documentElement.style.overflow = 'hidden';
  } else {
    document.documentElement.style.overflow = 'auto';
  }
});

const handleClose = () => {
  showAddTripModal.value = false;
  createdSuccess.value = false;
  formError.value = '';
  createdTripSlug.value = null;
  tripName.value = '';
  selectedLocation.value = [];
  // selectedDates.dateFrom = null as any;
  // selectedDates.dateTo = null as any;
  emit('update:show', false);
};

const handleContinue = () => {
  // Close and reset local state
  showAddTripModal.value = false;
  createdSuccess.value = false;
  createdTripSlug.value = null;
  tripName.value = '';
  selectedLocation.value = [];
  selectedDates.dateFrom = null as any;
  selectedDates.dateTo = null as any;
};

const handleGoToTrip = () => {
  if (!createdTripSlug.value) return;
  // Navigate to trip details page
  router.push(`/my/trip/${createdTripSlug.value}`);
  // also close & reset
  showAddTripModal.value = false;
  createdSuccess.value = false;
  createdTripSlug.value = null;
};

watch(() => tripName.value, (newName) => {
  if (newName) {
    formError.value = '';
    tripExists.value = false;
  }
});

</script>

<template>
<CustomModal :show="showAddTripModal" classes="w-1/6" @close="handleClose" :mobile-fullscreen="false" :custom-index="9999" extra-classes="h-fit" drawerHeight="fit-content">
<div class="flex flex-col p-0 sm:p-6 sm:min-w-[446px] ">
  <div class="cursor-pointer self-end close-div hidden sm:block" type="default" @click="handleClose">
    <img src="@/assets/icons/svg/Close_16x16.svg" alt="Close" width="18"/>
  </div>
  <div class="flex flex-col py-8 sm:px-4">
    <div class="responsive-title-4 pb-10 font-semibold">{{ t('createNewTrip') }}</div>
    <div class="flex flex-col">
      <VTextField v-model="tripName"
                  type="name"
                  :label="$t('addTripName')"
                  class="mb-5"
                  :class="createdSuccess ? 'text-black opacity-100 pointer-events-none' : ''"
                  variant="underlined"
                  :disabled="createdSuccess">
      </VTextField>
    </div>
    <div class="font-bold responsive-title-6"> {{ t('destination') }}</div>

    <div class="relative">
      <DestinationInput
          v-model="selectedLocation"
          :class="'mb-4'"
          :max-visible="5"
          :prefill-label="prefillLabel"
          :locked="isPrefilledLocation"
          :from-trip-add-modal="true"
      />
      <div v-if="isPrefilledLocation" class="absolute inset-0 z-[9999] pointer-events-auto" aria-hidden="true"></div>
    </div>
    <div class="relative flex flex-col">
      <div class="font-bold responsive-title-6"> {{ t('when') }}</div>

      <DateInput
          v-model:dateFrom="selectedDates.dateFrom"
          v-model:dateTo="selectedDates.dateTo"
          :has-icon="true"
          variant="underlined"
          :show-tabs="false"
          :columns="1"
          :rows="1"
          :closeOnSelect="true"
          :is-in-trip-add-modal="true"
          :calendarClass="'scale-[1.0]'"
      >
      </DateInput>
      <div v-if="isPrefilledDates" class="absolute inset-0 z-[9999] pointer-events-auto"></div>
    </div>
    <div v-if="formError" class="text-notifications-red responsive-body-xsmall">
      {{ formError }}
    </div>
    <div class="mt-5">
      <template v-if="!createdSuccess">
        <div class="flex gap-3 justify-between">
          <CustomButton
            type="black"
            :outlined="false"
            :rounded="true"
            size="medium"
            class="self-start w-28 h-13"
            :disabled="isCreatingTrip || tripExists"
            @click="onBook"
          >
            <template v-if="isCreatingTrip">
              <VueSpinner :size="20" color="white"/>
            </template>
            <template v-else>
              {{ t('create') }}
            </template>
          </CustomButton>
        </div>
      </template>
      <template v-else>
        <div class="text-green-600 responsive-body-small mb-3">
          {{ t('tripAddModal.createdSuccess') || 'Trip created successfully!' }}
        </div>
        <div class="flex gap-3 justify-between">
          <CustomButton
            type="black"
            :outlined="false"
            :rounded="true"
            size="medium"
            class="w-36 h-13"
            :disabled="!createdTripSlug"
            @click="handleGoToTrip"
          >
            {{ t('goToTrip') }}
          </CustomButton>
          <CustomButton
            type="black"
            :outlined="false"
            :rounded="true"
            size="medium"
            class="w-36 h-13"
            @click="handleContinue"
          >
            {{ t('continue') }}
          </CustomButton>
        </div>
      </template>
    </div>
  </div>

</div>
</CustomModal>
</template>

<style>
.v-field--disabled {
  opacity: 1 !important;
  color: black !important;
}
</style>
